import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItemButton,
  Divider,
  Skeleton,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import uiConfig from "../../config/uiConfig";

import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../services/notificationService";

function formatRelativeTime(value) {
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) {
    return "Just now";
  }
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }

  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString();
}

function NotificationsDrawer({ open, onClose, employeeId, onUnreadCountChange }) {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!employeeId) {
      return;
    }

    try {
      setLoading(true);
      const data = await getNotifications(employeeId);
      setNotifications(data);
    } catch (error) {
      console.error("Notifications Error:", error);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, loadNotifications]);

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      try {
        await markNotificationRead(notification.notification_id);
        setNotifications((prev) =>
          prev.map((item) =>
            item.notification_id === notification.notification_id
              ? { ...item, is_read: true }
              : item
          )
        );
        onUnreadCountChange?.((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Mark Read Error:", error);
      }
    }

    if (notification.link) {
      onClose();
      navigate(notification.link);
    }
  };

  const handleMarkAllRead = async () => {
    if (!employeeId) {
      return;
    }

    try {
      await markAllNotificationsRead(employeeId);
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      onUnreadCountChange?.(0);
    } catch (error) {
      console.error("Mark All Read Error:", error);
    }
  };

  const hasUnread = notifications.some((item) => !item.is_read);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}>
            Notifications
          </Typography>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Mark all as read">
              <span>
                <IconButton onClick={handleMarkAllRead} disabled={!hasUnread}>
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <IconButton onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {loading && (
            <Box sx={{ p: 2.5 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={`notification-skeleton-${index}`} sx={{ mb: 2 }}>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
              ))}
            </Box>
          )}

          {!loading && notifications.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#94a3b8",
                p: 4,
                textAlign: "center",
              }}
            >
              <NotificationsNoneIcon sx={{ fontSize: 40, mb: 1.5, color: "#cbd5e1" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#475569" }}>
                No notifications yet
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.5 }}>
                You&apos;ll see updates here when you&apos;re assigned to tickets or projects.
              </Typography>
            </Box>
          )}

          {!loading && notifications.length > 0 && (
            <List disablePadding>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.notification_id}>
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      alignItems: "flex-start",
                      px: 2.5,
                      py: 1.5,
                      gap: 1.2,
                      background: notification.is_read ? "transparent" : "#eff6ff",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        mt: 0.7,
                        flexShrink: 0,
                        background: notification.is_read ? "transparent" : uiConfig.buttonColor,
                      }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "#0f172a",
                          fontWeight: notification.is_read ? 500 : 700,
                        }}
                      >
                        {notification.message}
                      </Typography>

                      <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.4 }}>
                        {formatRelativeTime(notification.created_at)}
                      </Typography>
                    </Box>
                  </ListItemButton>

                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {!loading && notifications.length > 0 && (
          <Box sx={{ p: 1.5, borderTop: "1px solid #e5e7eb" }}>
            <Button
              fullWidth
              size="small"
              onClick={loadNotifications}
              sx={{ textTransform: "none", color: "#64748b" }}
            >
              Refresh
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default NotificationsDrawer;
